// src/pages/CacheAdminPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ApiService from '../services/api';

const CacheAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheEntries, setCacheEntries] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, hash: null });
  const [refreshing, setRefreshing] = useState(false);

  // Load cache entries
  const loadCacheEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ApiService.listCache();
      
      if (response && response.cache) {
        // Convert the object to an array for the table
        const entries = Object.entries(response.cache).map(([hash, taskId]) => ({
          hash,
          taskId
        }));
        
        setCacheEntries(entries);
      } else {
        setCacheEntries([]);
      }
    } catch (error) {
      console.error('Error loading cache entries:', error);
      setError('Failed to load cache entries. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Delete a cache entry
  const deleteEntry = async (hash) => {
    try {
      setRefreshing(true);
      await ApiService.deleteFromCache(hash);
      // Refresh the list after deletion
      await loadCacheEntries();
    } catch (error) {
      console.error('Error deleting cache entry:', error);
      setError('Failed to delete cache entry. Please try again later.');
    } finally {
      setRefreshing(false);
      setDeleteDialog({ open: false, hash: null });
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (hash) => {
    setDeleteDialog({ open: true, hash });
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, hash: null });
  };

  // Load cache entries on first page load
  useEffect(() => {
    loadCacheEntries();
  }, []);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Cache Management
      </Typography>
      
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Stored Analyses
          </Typography>
          
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={loadCacheEntries}
            disabled={loading || refreshing}
          >
            Refresh
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : cacheEntries.length === 0 ? (
          <Alert severity="info">
            No cache entries found.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Hash</TableCell>
                  <TableCell>Task ID</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cacheEntries.map((entry) => (
                  <TableRow key={entry.hash}>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          maxWidth: '220px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {entry.hash}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          maxWidth: '220px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {entry.taskId}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => openDeleteDialog(entry.hash)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Confirmation dialog for deletion */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Delete Cache Entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this cache entry? The next analysis for this file will be performed anew.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={refreshing}>
            Cancel
          </Button>
          <Button 
            onClick={() => deleteEntry(deleteDialog.hash)} 
            color="error" 
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CacheAdminPage;